import { useState, useEffect } from 'react';
import { useOtpLogin } from './useOtpLogin';
import { useCaptureDetailsApi, OtpStatus } from './useCaptureDetailsApi';
import { ApiError } from './api.service';

type Step = 'EMAIL' | 'OTP' | 'SUCCESS';

export const useCaptureDetailsFlow = () => {
  const urlParams = new URLSearchParams(window.location.search);
  const authStateKey = urlParams.get('auth_state_key') || '';

  const { skipOtp, fetchUserDetails } = useCaptureDetailsApi();

  const [stepState, setStepState] = useState<Step>((urlParams.get('step') as Step) || 'EMAIL');

  const setStep = (newStep: Step) => {
    setStepState(newStep);
    const params = new URLSearchParams(window.location.search);
    params.set('step', newStep);
    window.history.pushState({}, '', `${window.location.pathname}?${params.toString()}`);
  };

  const step = stepState;
  const [username, setUsername] = useState<string>('');
  const [otpStatus, setOtpStatus] = useState<OtpStatus | null>({
    skipAllowed: true,
    skipsRemaining: 3,
    totalAttempts: 3,
  });
  const [showSkipPolicy, setShowSkipPolicy] = useState<boolean>(true);
  const [isSkipLoading, setIsSkipLoading] = useState<boolean>(false);

  const {
    email,
    setEmail,
    isOtpSent,
    otp,
    timer,
    canResend,
    error,
    setError,
    isLoading,
    isResending,
    isLockedOut,
    handleChange,
    handlePaste,
    handleContinue,
    handleResend,
    handleCancel,
    handleSendOtp,
    canContinue,
  } = useOtpLogin(urlParams.get('email') || '');

  useEffect(() => {
    if (authStateKey) {
      const loadUserDetails = async () => {
        try {
          const data = await fetchUserDetails(authStateKey);
          if (data.redirectUri) {
            window.location.replace(data.redirectUri);
            return;
          }
          if (data.error) {
            window.location.replace(`/error?error=${encodeURIComponent(data.error)}`);
            return;
          }
          if (data.userName) {
            setUsername(data.userName);
          }
          if (data.legacyAppEmail) {
            setEmail(data.legacyAppEmail);
          }
          if (step === 'OTP' && !data.legacyAppEmail) {
            setStep('EMAIL');
          }
          if (typeof data.skipCount === 'number') {
            setOtpStatus({
              skipAllowed: data.skipCount > 0,
              skipsRemaining: data.skipCount,
              totalAttempts: 3,
            });
          }
        } catch (err) {
          console.error('Failed to load user details', err);
          if (err instanceof ApiError && err.redirectUri) {
            window.location.replace(err.redirectUri);
          } else {
            const backendError =
              err instanceof ApiError
                ? err.error || (err.message && err.message !== 'Something went wrong' ? err.message : '')
                : '';
            const errorQuery = backendError ? `?error=${encodeURIComponent(backendError)}` : '';
            window.location.replace(`/error${errorQuery}`);
          }
        }
      };
      loadUserDetails();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authStateKey]);

  const handleSkip = async () => {
    setIsSkipLoading(true);
    setError(null);
    try {
      const data = await skipOtp(authStateKey);
      if (data?.redirectUri) {
        window.location.replace(data.redirectUri);
      }
    } catch (e: unknown) {
      console.error('Failed to notify skip event to backend', e);
      const message = e instanceof Error ? e.message : 'Failed to skip. Please try again.';
      setError(message);
    } finally {
      setIsSkipLoading(false);
    }
  };

  const onSendOtpClick = async () => {
    await handleSendOtp();
  };

  useEffect(() => {
    if (isOtpSent && step === 'EMAIL') {
      setStep('OTP');
    }
  }, [isOtpSent, step]);

  const onChangeEmail = () => {
    handleCancel();
    setStep('EMAIL');
  };

  return {
    step,
    setStep,
    username,
    email,
    setEmail,
    otp,
    timer,
    canResend,
    error,
    isLoading,
    isResending,
    isLockedOut,
    handleChange,
    handlePaste,
    handleContinue,
    handleResend,
    canContinue,
    otpStatus,
    showSkipPolicy,
    setShowSkipPolicy,
    handleSkip,
    isSkipLoading,
    onSendOtpClick,
    onChangeEmail,
  };
};
